import { ToggleButtonGroup as MuiToggleButtonGroup } from '@mui/material';
import React, { forwardRef } from 'react';
import { SxProps } from '../../utils';

type NativeDivProps = React.ComponentPropsWithoutRef<'div'>;

export interface ToggleButtonGroupProps<T> extends Omit<NativeDivProps, 'color' | 'onChange'> {
  /**
   * Value is required which means every toggle group must have a selected option. No non-selected state allowed.
   */
  value: T;
  /**
   * Whether the toggle button group is disabled
   */
  disabled?: boolean;
  /**
   * Whether the toggle button group takes up the full width of its container
   */
  fullWidth?: boolean;
  /**
   * Add props here.
   */
  onChange?: (event: React.MouseEvent<HTMLElement>, value: T) => void;
  /**
   * The system prop that allows defining system overrides as well as additional CSS styles.
   */
  sx?: SxProps;
}

export const ToggleButtonGroup = forwardRef(function ToggleButtonGroup<T>(
  { disabled, fullWidth, onChange, ...props }: ToggleButtonGroupProps<T>,
  ref: React.ForwardedRef<HTMLButtonElement>
) {
  // ToggleGroup should always have a value set. If a user attempts to deselect current option, discard onChange event without update.
  const onChangeHandler: ToggleButtonGroupProps<T>['onChange'] = (e, newValue) => {
    if (newValue === null) {
      return;
    }

    onChange?.(e, newValue);
  };

  return (
    <MuiToggleButtonGroup
      ref={ref}
      onChange={onChangeHandler}
      exclusive
      disabled={disabled}
      fullWidth={fullWidth}
      {...props}
    />
  );
}) as <T>(props: ToggleButtonGroupProps<T>, ref: React.ForwardedRef<HTMLButtonElement>) => React.ReactElement;
